import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import {
  provideRouter,
  withInMemoryScrolling,
  withNavigationErrorHandler,
  withRouterConfig,
} from '@angular/router';
import { handleStaleLazyChunkNavigationError } from './shared/helpers/lazy-chunk-reload.helper';

import { routes } from './app.routes';
import { ApmUserService } from '@stores/apm-user.service';
import { apmInstance } from './apm.config';
import {
  ApmErrorHandler,
  ApmModule,
  ApmService,
} from '@elastic/apm-rum-angular';
import { Title } from '@angular/platform-browser';
import { CurrencyPipe, DatePipe, TitleCasePipe } from '@angular/common';
import { AmountFormatter } from './core/pipes';
import { HttpsService } from './shared/services/base.service';
import { CommonService } from './shared/services/common.service';
import { apiList } from '@helpers/apilist';
import { CookieService } from 'ngx-cookie-service';
import { GlobalToastrService } from './shared/services/toastr.service';
import { BrandingService } from './shared/services/branding.service';
import {
  DashBoardResolveService,
  StepResolveService,
} from './auth/helpers/step.resolve.guard';
import { ToasterService } from './shared/services/toaster.service';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import { environment } from '@environments/environment';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { LoadingInterceptor } from '@helpers/loader.interceptor';
import { ErrorInterceptor, JwtInterceptor } from './auth/helpers';
import { provideAnimations } from '@angular/platform-browser/animations';
import { AppFactoryModule } from './app-factory.module';
import { ToastrModule } from 'ngx-toastr';
import { CoreModule } from '@core/core.module';
import { coreConfig } from './app-config';
import { CoreSidebarModule } from '@core/components/core-sidebar/core-sidebar.module';

export function startupServiceFactory(
  BrandingService: BrandingService
): Function {
  return () => BrandingService.load();
}

export function initializeApm(apmUserService: ApmUserService) {
  return () => apmUserService.setUserContext();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
      withNavigationErrorHandler(handleStaleLazyChunkNavigationError)
    ),
    provideHttpClient(
      withInterceptorsFromDi()
    ),
    provideAnimations(),
    importProvidersFrom(
      ApmModule,
      AppFactoryModule.forRoot(),

      //NgBootstrap
      ToastrModule.forRoot({
        timeOut: 4000,
      }),

      // Core modules
      CoreModule.forRoot(coreConfig),
      CoreSidebarModule,

      // App modules
      RecaptchaV3Module
    ),
    ApmUserService,
    { provide: ApmService, useValue: apmInstance },
    provideAppInitializer(() => {
        const initializerFn = (initializeApm)(inject(ApmUserService));
        return initializerFn();
      }),
    {
      provide: ErrorHandler,
      useClass: ApmErrorHandler,
    },
    Title,
    DatePipe,
    CurrencyPipe,
    AmountFormatter,
    TitleCasePipe,
    HttpsService,
    CommonService,
    apiList,
    CookieService,
    GlobalToastrService,
    BrandingService,
    StepResolveService,
    DashBoardResolveService,
    ToasterService,
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptchaSiteKey },
    provideAppInitializer(() => {
        const initializerFn = (startupServiceFactory)(inject(BrandingService));
        return initializerFn();
      }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
  ],
};
